import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

import { Botao, EmptyState, ErrorState, Field, Input, Modal, Skeleton } from '../../../ui';
import ResumoRegistro from '../../ti/ResumoRegistro';
import { formatPlaca } from '../../../utils/mascaras';
import { extrairMensagemErro } from '../../../utils/apiError';
import { fetchCarroPorPlaca } from '../../../api/carro/carroApi';
import { enviarVeiculoParaSgaPorPlaca } from '../../../api/sga-veiculo/sga-veiculoApi';

/**
 * Envio manual de um veículo ao SGA (tela `/ti`).
 * Reescrito sobre o design system na at-67 (frente D/D5).
 *
 * 🔴 **Três correções de comportamento:**
 *
 *   1. **Placa inexistente não dizia nada.** O `catch` mostrava um toast e a lista
 *      ficava **vazia, sem texto nenhum** — nem um "Nenhum veículo encontrado"
 *      (o modal de associado tinha; este, não). Agora 404 é `EmptyState`
 *      ("não está no SAAC") e qualquer outra falha é `ErrorState` com "Tentar
 *      novamente": são problemas diferentes, com ações diferentes.
 *   2. **A "lista" de um item só sumiu.** A busca é por placa exata e devolve no
 *      máximo um veículo — escolher numa lista de um era um clique sem informação
 *      nova. O resultado já é o **resumo**, com "Trocar veículo" para voltar.
 *   3. **O `FullScreenLoader` dentro do modal** (um overlay de tela cheia por cima
 *      do overlay do diálogo, a cada busca) virou `Skeleton` no corpo.
 *
 * ⚠️ **A chamada ao SGA não mudou**: `enviarVeiculoParaSgaPorPlaca(placa)`, e o
 * modal fecha no sucesso.
 *
 * 📌 O saneamento da placa passou a usar `formatPlaca` (`utils/mascaras.ts`) em vez
 * da cadeia de `replace` escrita aqui — mesmo resultado, uma fonte só.
 */

type VeiculoEncontrado = {
  id: string;
  placa: string;
  modelo?: string;
  chassi?: string;
  renavam?: string;
  anoModelo?: number;
  anoFabricacao?: number;
  codigoVeiculo?: string;
};

/** `null` = nada buscado ainda. */
type Resultado =
  | null
  | { tipo: 'achou'; veiculo: VeiculoEncontrado }
  | { tipo: 'nao-existe' }
  | { tipo: 'erro'; mensagem: string };

interface ModalEnvioDeVeiculoProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmEnviar?: (veiculo: VeiculoEncontrado) => Promise<void> | void;
}

const ModalEnvioDeVeiculo: React.FC<ModalEnvioDeVeiculoProps> = ({
  isOpen,
  onClose,
  onConfirmEnviar,
}) => {
  const [termo, setTermo] = useState('');
  const [resultado, setResultado] = useState<Resultado>(null);
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTermo('');
    setResultado(null);
    setBuscando(false);
    setEnviando(false);
  }, [isOpen]);

  const buscar = async () => {
    const placa = termo.trim();
    if (!placa) return;

    setBuscando(true);
    setResultado(null);

    try {
      const carro = await fetchCarroPorPlaca(placa);
      setResultado({
        tipo: 'achou',
        veiculo: {
          id: carro?.id ?? placa,
          placa: carro?.placa ?? placa,
          modelo: carro?.modelo,
          chassi: carro?.chassi,
          renavam: carro?.renavam,
          anoModelo: carro?.anoModelo,
          anoFabricacao: carro?.anoFabricacao,
          codigoVeiculo: carro?.codigoVeiculo,
        },
      });
    } catch (falha) {
      /* ⚠️ `fetchCarroPorPlaca` relança o AxiosError CRU — é isso que permite
         separar "não existe aqui" (404) de "não deu para perguntar" (rede, 500).
         Tratar os dois igual foi o defeito nº 1 do cabeçalho. */
      const status = (falha as AxiosError)?.response?.status;
      if (status === 404) {
        setResultado({ tipo: 'nao-existe' });
      } else {
        setResultado({
          tipo: 'erro',
          mensagem: extrairMensagemErro(falha, 'Não foi possível buscar o veículo.'),
        });
      }
    } finally {
      setBuscando(false);
    }
  };

  const confirmarEnvio = async () => {
    if (resultado?.tipo !== 'achou') return;

    const { veiculo } = resultado;
    const placa = (veiculo.placa ?? '').trim();
    if (!placa) {
      toast.error('Veículo selecionado não possui placa.');
      return;
    }

    setEnviando(true);
    try {
      if (onConfirmEnviar) {
        await onConfirmEnviar(veiculo);
      } else {
        await enviarVeiculoParaSgaPorPlaca(placa);
      }

      toast.success('Envio solicitado com sucesso');
      onClose();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao enviar veículo'));
    } finally {
      setEnviando(false);
    }
  };

  const achou = resultado?.tipo === 'achou' ? resultado.veiculo : null;

  return (
    <Modal
      aberto={isOpen}
      titulo="Envio de Veículo"
      descricao="Envia ao SGA um veículo já cadastrado no SAAC."
      ocupado={enviando}
      aoFechar={onClose}
      rodape={
        achou ? (
          <>
            <Botao variante="secundario" onClick={onClose} disabled={enviando}>
              Cancelar
            </Botao>
            <Botao variante="primario" carregando={enviando} onClick={confirmarEnvio}>
              Confirmar envio
            </Botao>
          </>
        ) : (
          <Botao variante="secundario" onClick={onClose} disabled={buscando}>
            Fechar
          </Botao>
        )
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {!achou && (
          <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-end">
            <Field rotulo="Placa" className="sm:tw-flex-1">
              <Input
                value={termo}
                inputMode="text"
                autoCapitalize="characters"
                placeholder="ABC1D23"
                disabled={buscando}
                onChange={(evento) => setTermo(formatPlaca(evento.target.value))}
                onKeyDown={(evento) => {
                  if (evento.key === 'Enter') void buscar();
                }}
              />
            </Field>
            <Botao
              variante="primario"
              iconeEsquerda="busca"
              carregando={buscando}
              disabled={!termo.trim()}
              onClick={() => void buscar()}
              className="sm:tw-mb-0"
            >
              Buscar
            </Botao>
          </div>
        )}

        {buscando && <Skeleton linhas={1} altura="tw-h-20" />}

        {!buscando && resultado?.tipo === 'nao-existe' && (
          <EmptyState
            icone="veiculo"
            titulo="Nenhum veículo com esta placa no SAAC"
            descricao="Confira a placa. Se o veículo existe só no SGA, use a importação por placa na tela de vistoria ou de substituição."
          />
        )}

        {!buscando && resultado?.tipo === 'erro' && (
          <ErrorState
            mensagem="Não foi possível buscar o veículo."
            detalhe={resultado.mensagem}
            aoTentarNovamente={() => void buscar()}
          />
        )}

        {achou && (
          <ResumoRegistro
            linhas={[
              { rotulo: 'Placa', valor: achou.placa || '—' },
              { rotulo: 'Modelo', valor: achou.modelo || '—' },
              { rotulo: 'Chassi', valor: achou.chassi || '—' },
              { rotulo: 'Renavam', valor: achou.renavam || '—' },
              { rotulo: 'Código no SGA', valor: achou.codigoVeiculo || '—' },
            ]}
            acao={
              <Botao
                variante="fantasma"
                tamanho="sm"
                disabled={enviando}
                onClick={() => {
                  setResultado(null);
                  setTermo('');
                }}
              >
                Trocar veículo
              </Botao>
            }
          />
        )}
      </div>
    </Modal>
  );
};

export default ModalEnvioDeVeiculo;
